<template>
  <div class="data text-left mt-6">
    <div class="close d-flex flex-row align-center pt-5 ml-4 ml-md-0" @click="backToOrigin">
      <img :src="closeIcon" class="icon mr-4"/><span class="label">{{ $t('page.go_back_to_search') }}</span>
    </div>
    <div class="compare-header mt-6 px-4 px-md-0">
      <div class="header1">{{ $t('compare_list.header') }} {{$t(`donor.${getContext}.gen_plural`)}}</div>
      <div class="counter mt-4" v-if="elements.length">{{$t('compare_list.counter', elements.length, {count: elements.length})}}</div>
    </div>
    <v-main class="compare-view d-flex pa-0 py-md-6 px-md-8 flex-md-row flex-column my-0 mb-md-16 mt-md-6" v-if="!loading && elements.length">
      <div class="compare-wrapper" v-if="!$vuetify.display.mobile">
        <div class="element" v-for="(element, index) in elements" :key="index"
          :class="'div'+(index+1)">
          <div
            class="close-button text-center mb-7"
            @click="removeFromCompareList(element)"  
          >
            <v-icon color="primary">mdi-close</v-icon>
          </div>
          <div class="photo-wrapper mb-2">
            <img :src="getPlaceholder(element)" class="photo" />
          </div>
          <div class="id-wrapper text-center mb-6">
            <div class="donor-name mb-2">{{getDonorName(element)}}</div>
            <span>
              <div class="id px-2 py-1">
                {{element._source.donor_process.donor_nickname}}
              </div>
              <v-icon :icon="isFavourite(element) ? 'mdi-heart' : 'mdi-heart-outline'" size="24" class="icon ml-2" color="primary" @click="toggleFavourite(element)"></v-icon>
            </span>
          </div>
          <div class="product-name mb-4">{{getProductName(element)}}</div>
          <div class="price mb-6">
            {{ $n(prices[element._source.material.type].price_brutto, 'currency', 'pl') }}
          </div>
          <v-dialog
            v-model="dialogOpen.find(e => e.id == element._source.id).value"
            :fullscreen="$vuetify.display.mobile"
          >
            <template v-slot:activator="{ attrs }">
              <v-btn plain elevation="0"
                v-bind="attrs"
                @click="showDialog(element)"
                color="primary"
                height="40"
                width="100%"
                rounded
              > 
                {{ $t('reservation.reserve_btn') }}
              </v-btn>
            </template>
            <div class="close-dialog" :class="{'mobile': $vuetify.display.mobile}" @click="hideDialog(element)">
                <v-icon color="primary">mdi-close</v-icon>
            </div>
            <add-to-cart-component
              :donor-details="{...element._source, ...element.fields}"
              :maxTrueReservationCount="element.fields.max_true_reservation_count[0]"
              :dialog="true"
              :age="element.fields.age[0]"
              @close="hideDialog(element)"
            >
            </add-to-cart-component>
          </v-dialog>
        </div>
        <div class="legend div4">
          <div class="legend-header d-flex flex-row align-center mb-2">
            <img :src="eye_icon"/>&nbsp;&nbsp;<span>{{$t('donor.phenotype.physical_features_label').toUpperCase()}}</span>
          </div>
          <div class="legend-data-wrapper">
            <div class="data mb-4">{{$t('donor.phenotype.eye_color')}}</div>
            <div class="data mb-4">{{$t('donor.phenotype.hair_color')}}</div>
            <div class="data mb-4">{{$t('donor.phenotype.hair_shape')}}</div>
            <div class="data mb-4">{{$t('donor.phenotype.height')}}</div>
            <div class="data">{{$t('donor.phenotype.body_type')}}</div>
          </div>

          <div class="legend-header d-flex flex-row align-center mb-4 mt-16">
            <img :src="blood_icon"/>&nbsp;&nbsp;<span>{{$t('donor.phenotype.blood_label')}}</span>
          </div>
          <div class="legend-data-wrapper">
            <div class="data mb-4">{{$t('donor.phenotype.blood_type')}}</div>
            <div class="data">{{$t('donor.phenotype.blood_fh_factor')}}</div>
          </div>

          <div class="legend-header d-flex flex-row align-center mb-4 mt-16">
            <img :src="group_icon"/>&nbsp;&nbsp;<span>{{$t('donor.phenotype.origin_label')}}</span>
          </div>
          <div class="legend-data-wrapper">
            <div class="data">{{$t('donor.phenotype.skin_color')}}</div>
          </div>
        </div>
        <div class="element" v-for="(element, index) in elements" :key="index"
          :class="'div'+(index+5)">
          <div class="legend-header d-flex flex-row align-center mb-2">&nbsp;</div>
          <div class="data-wrapper">
            <div class="data mb-4">{{ $t('phenotype.eye_color.' + element._source.material.type + '.' + element._source.phenotype.eye_color) }}</div>
            <div class="data mb-4">
              {{$t('phenotype.hair_color.' + element._source.material.type + '.' + element._source.phenotype.hair_color).match(/([^\()]+)/)[0] }}
              <span class="sub-entry" v-if="$t('phenotype.hair_color.' + element._source.material.type + '.' + element._source.phenotype.hair_color).match(/\((.*?)\)/)">{{$t('phenotype.hair_color.' + element._source.material.type + '.' + element._source.phenotype.hair_color).match(/\((.*?)\)/)[0]}}</span> 
            </div>
            <div class="data mb-4">{{ $t('phenotype.hair_structure.' + element._source.material.type + '.' + element._source.phenotype.hair_structure) }}</div>
            <div class="data mb-4">{{element._source.phenotype.height}} {{$t('cm')}}</div>
            <div class="data">
              {{$t('phenotype.silhouette.' + element._source.material.type + '.' + element._source.phenotype.silhouette).match(/([^\s]+)/)[0] }}
              <span class="sub-entry">{{$t('phenotype.silhouette.' + element._source.material.type + '.' + element._source.phenotype.silhouette).match(/\((.*?)\)/)[0] }}</span> 
            </div>
          </div>

          <div class="legend-header d-flex flex-row align-center mb-4 mt-16">&nbsp;</div>
          <div class="data-wrapper">
            <div class="data mb-4">{{ $t('phenotype.blood_type.' + element._source.material.type + '.' + element._source.phenotype.blood_type) }}</div>
            <div class="data">{{ $t('phenotype.blood_rh_factor.' + element._source.material.type + '.' + element._source.phenotype.blood_rh_factor) }}</div>
          </div>

          <div class="legend-header d-flex flex-row align-center mb-4 mt-16">&nbsp;</div>
          <div class="data-wrapper">
            <div class="data mb-4">
              {{$t('phenotype.skin_color.' + element._source.material.type + '.' + element._source.phenotype.skin_color).match(/([^\s]+)/)[0] }}
              <span class="sub-entry" v-if="$t('phenotype.skin_color.' + element._source.material.type + '.' + element._source.phenotype.skin_color).match(/\((.*?)\)/)">{{$t('phenotype.skin_color.' + element._source.material.type + '.' + element._source.phenotype.skin_color).match(/\((.*?)\)/)[0]}}</span> 
            </div>
          </div>
        </div>
      </div>
      <div v-else class="compare-wrapper-mobile pa-4">
        <swiper
        :slides-per-view="1.5"
        :space-between="50"
        :slides-offset-after="0"
        :slide-active-class="'slide-active'"
        :resistance="true"
        :resistance-ratio="0"
        @swiper="onSwiper"
        @slideChange="onSlideChange"
      >
        <swiper-slide v-for="(element, index) in elements" :key="index">
          <div class="element-mobile">
            <div
              class="close-button text-center mb-7"
              @click="removeFromCompareList(element)"  
            >
              <v-icon color="primary">mdi-close</v-icon>
            </div>
            <div class="photo-wrapper mb-2">
              <img :src="getPlaceholder(element)" class="photo" />
            </div>
            <div class="id-wrapper text-center mb-6">
              <span>
                <v-icon :icon="isFavourite(element) ? 'mdi-heart' : 'mdi-heart-outline'" size="20" class="icon" color="primary" @click="toggleFavourite(element)"></v-icon>
              </span>
              <div class="id px-2 py-1">{{element._source.donor_process.donor_nickname}}</div>
            </div>
            <div class="product-name mb-4">{{getProductName(element)}}</div>
            <div class="price mb-6">
              {{ $n(prices[element._source.material.type].price_brutto, 'currency', 'pl') }}
            </div>
            <v-dialog
              v-model="dialogOpen.find(e => e.id == element._source.id).value"
              :fullscreen="$vuetify.display.mobile"
            >
              <template v-slot:activator="{ attrs }">
                <v-btn plain elevation="0"
                  v-bind="attrs"
                  @click="showDialog(element)"
                  color="primary"
                  height="40"
                  width="86%"
                  rounded
                >
                  {{ $t('reservation.reserve_btn') }}
                </v-btn>
              </template>
              <div class="close-dialog" :class="{'mobile': $vuetify.display.mobile}" @click="hideDialog(element)">
                  <v-icon color="primary">mdi-close</v-icon>
              </div>
              <add-to-cart-component
                :donor-details="{...element._source, ...element.fields}"
                :maxTrueReservationCount="element.fields.max_true_reservation_count[0]"
                :dialog="true"
                :age="element.fields.age[0]"
                @close="hideDialog(element)"
              ></add-to-cart-component>
            </v-dialog>
            <div class="legend-header d-flex flex-row align-center mb-2 mt-14">
              <img :src="eye_icon"/>&nbsp;&nbsp;<span>{{$t('donor.phenotype.physical_features_label')}}</span>
            </div>
            <div class="data-wrapper">
              <div class="legend-data-wrapper">
                <div class="data">{{$t('donor.phenotype.eye_color')}}</div>
              </div>
              <div class="data mb-2">{{ $t('phenotype.eye_color.' + element._source.material.type + '.' + element._source.phenotype.eye_color) }}</div>
              <div class="legend-data-wrapper">
                <div class="data">{{$t('donor.phenotype.hair_color')}}</div>
              </div>
              <div class="data mb-2">
                {{$t('phenotype.hair_color.' + element._source.material.type + '.' + element._source.phenotype.hair_color).match(/([^\()]+)/)[0] }}
                <span class="sub-entry" v-if="$t('phenotype.hair_color.' + element._source.material.type + '.' + element._source.phenotype.hair_color).match(/\((.*?)\)/)">{{$t('phenotype.hair_color.' + element._source.material.type + '.' + element._source.phenotype.hair_color).match(/\((.*?)\)/)[0]}}</span> 
              </div>
              <div class="legend-data-wrapper">
                <div class="data">{{$t('donor.phenotype.hair_shape')}}</div>
              </div>
              <div class="data mb-2">{{ $t('phenotype.hair_structure.' + element._source.material.type + '.' + element._source.phenotype.hair_structure) }}</div>
              <div class="legend-data-wrapper">
                <div class="data">{{$t('donor.phenotype.height')}}</div>
              </div>
              <div class="data mb-2">{{element._source.phenotype.height}} {{$t('cm')}}</div>

              <div class="legend-data-wrapper">
                <div class="data">{{$t('donor.phenotype.body_type')}}</div>
              </div>
              <div class="data">
                {{$t('phenotype.silhouette.' + element._source.material.type + '.' + element._source.phenotype.silhouette).match(/([^\s]+)/)[0] }}
                <span class="sub-entry">{{$t('phenotype.silhouette.' + element._source.material.type + '.' + element._source.phenotype.silhouette).match(/\((.*?)\)/)[0] }}</span> 
              </div>
            </div>

            <div class="legend-header d-flex flex-row align-center mb-2 mt-12">
              <img :src="blood_icon"/>&nbsp;&nbsp;<span>{{$t('donor.phenotype.blood_label')}}</span>
            </div>
            <div class="data-wrapper">
              <div class="legend-data-wrapper">
                <div class="data">{{$t('donor.phenotype.blood_type')}}</div>
              </div>
              <div class="data mb-2">{{ $t('phenotype.blood_type.' + element._source.material.type + '.' + element._source.phenotype.blood_type) }}</div>
              <div class="legend-data-wrapper">
                <div class="data">{{$t('donor.phenotype.blood_fh_factor')}}</div>
              </div>
              <div class="data">{{ $t('phenotype.blood_rh_factor.' + element._source.material.type + '.' + element._source.phenotype.blood_rh_factor) }}</div>
            </div>

            <div class="legend-header d-flex flex-row align-center mb-2 mt-12">
              <img :src="group_icon"/>&nbsp;&nbsp;<span>{{$t('donor.phenotype.origin_label')}}</span>
            </div>
            <div class="data-wrapper">
              <div class="legend-data-wrapper">
                <div class="data">{{$t('donor.phenotype.skin_color')}}</div>
              </div>
              <div class="data mb-2">
                {{$t('phenotype.skin_color.' + element._source.material.type + '.' + element._source.phenotype.skin_color).match(/([^\s]+)/)[0] }}
                <span class="sub-entry" v-if="$t('phenotype.skin_color.' + element._source.material.type + '.' + element._source.phenotype.skin_color).match(/\((.*?)\)/)">{{$t('phenotype.skin_color.' + element._source.material.type + '.' + element._source.phenotype.skin_color).match(/\((.*?)\)/)[0]}}</span> 
              </div>
            </div>
          </div>
        </swiper-slide>
      </swiper>
      </div>
    </v-main>
    <v-main v-else class="compare-view d-flex pa-0 py-md-6 px-md-16 flex-md-row flex-column my-0 mb-md-16 mt-md-6">
      <div class="info-wrapper" v-if="!elements.length && !loading">
        <div :class="['info d-flex flex-column justify-center mt-4 mb-2 mx-4 py-12 px-9', $vuetify.display.mobile ? 'mobile' : 'text-left']">
          <div class="header mb-6 mb-md-4">
            {{$t('compare_list.empty_header')}}
          </div>
          <div class="subheader">
            {{$t('compare_list.empty_subheader')}}
          </div>
        </div>
      </div>

      <v-progress-circular
          v-else
          indeterminate
          color="primary"
          :size="50"
          :width="5"
          class="loader my-10"
      ></v-progress-circular>
    </v-main>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters, useStore } from "vuex";
import { computed } from "vue";
import AddToCartComponent from "@/components/AddToCartComponent.vue";
import { Swiper, SwiperSlide } from 'swiper/vue';

// Import Swiper styles
import 'swiper/css';

export default {
  data() {
    return {
      elements: [],
      prices: null,
      loading: false,
      closeIcon: require('../assets/icons/chevron-left.svg'),
      store: useStore(),
      dialogOpen: [],
    }
  },
  components: {
    AddToCartComponent,
    Swiper,
    SwiperSlide
  },
  setup() {
    const store = useStore();
    const eye_icon = computed({
      get: () => { return require('../assets/icons/eye.svg');}
    })
    const blood_icon = computed({
      get: () => { return require('../assets/icons/blood.svg');}
    })
    const group_icon = computed({
      get: () => { return require('../assets/icons/group.svg');}
    })
    return {
        eye_icon,
        blood_icon,
        group_icon,
        oidcUser: computed(() => store.getters['oidcStore/oidcUser']),
    }
  },
  computed: {
    ...mapGetters('compareModule',['getContext','getCompareListByMaterialType']),
    ...mapGetters('favouritesModule', ['getFavourites'])
  },
  mounted() {
    this.getElements();
    this.$store.dispatch('breadcrumbModule/setRoute', [
      {
        name: 'breadcrumb.results',
        url: '/results'
      },
      {
        name: 'breadcrumb.compare',
        url: ''
      }
    ])
  },
  watch: {
    '$route.query.ids': {
      handler() {
        
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    getElements() {
      this.loading = true;
      this.elements = [];
      let ids = [];
      if(this.$route.query.ids) {
        if(Array.isArray(this.$route.query.ids)) {
          ids = this.$route.query.ids;
        } else {
          ids = [this.$route.query.ids];
        }
      }
      let request = ids.map(id => {
        new Promise((resolve, reject) => {
          axios
          .get(`/api/donor/${id}/${this.getContext}/data`)
          .then((response) => {
            this.elements.push(response.data);
            resolve(response.data);
            this.dialogOpen.push({id: response.data._source.id, value: false})
          })
          .catch(err => {
            console.error(err);
            reject(err);
          })
        })
      })

      request.push(
        new Promise((resolve, reject) => {
          axios
            .get(`/api/donor/material_price`)
            .then((response) => {
                this.prices = response.data;
                resolve();
            })
            .catch(err => {
                console.error(err);
                reject();
            })
        })
      )

      Promise.all(request)
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        })
    },
    backToOrigin() {
        this.$router.push({name: 'resultsView'});
    },
    getDonorName(element){
      return this.$t(`donor.${element._source.material.type}.nom`)
    },
    getProductName(element){
      let name = this.$t(`reservation.${element._source.material.type}.product_name`, {size: element._source.material.package_size})
      if (element._source.material.type == 'mtSemen') {
        name += ' ' + this.$t(element._source.material.fitness_for_insemination)
      }
      return name
    },
    getPlaceholder(element){
      if(element._source.material.type == "mtCell")
                return require('../assets/img/placeholder_female.svg')
            else
                return require('../assets/img/placeholder_male.svg')
    },
    toggleFavourite(element){
      if(this.oidcUser) {
        if(this.isFavourite(element)){
          this.store.dispatch('favouritesModule/removeFavourite', {
              donor_product_key: element._source.id,
              material_type: element._source.material.type
          })
        } else {
          this.store.dispatch('favouritesModule/addFavourite', {
              donor_product_key: element._source.id,
              material_type: element._source.material.type
          })
        }
      }
    },
    isFavourite(element) {
      return this.getFavourites.find(e => e.donor_product_key == element._source.id && e.material_type == element._source.material.type);
    },
    showDialog(element) {
      this.dialogOpen.find(e => e.id == element._source.id).value = true;
    },
    hideDialog(element) {
      this.dialogOpen.find(e => e.id == element._source.id).value = false;
    },
    removeFromCompareList(element){
      this.$store.dispatch(
        'compareModule/removeFromCompareList',
        {
            id: element._source.donor_process.donor_nickname,
            pk: element._source.id,
            materialType: element._source.material.type})
      .then((result) => {
        if(result) {
          this.elements = this.elements.filter(e => e._source.id != element._source.id);
          this.$router.push({name: 'compareView', query: {ids: this.elements.map(e => e._source.id)}})
        }
      });
    }
  }
}

</script>

<style lang="scss" scoped>
.close-dialog {
  cursor: pointer;
  position: absolute;
  // top: 0px;
  // right: 0px;
}
.compare-header {
  max-width: 996px;
  margin: 0 auto;
  .header1 {
    @include nsb-gentium-bold;
    font-size: 28px !important;
    line-height: 32px;
  }
  .counter {
    @include nsb-font-container;
    font-size: 14px;
    line-height: 21px;
  }
}
.compare-view {
  max-width: 996px;
  margin: 0 auto;
  background: white;
}
.compare-wrapper {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, auto);
  grid-column-gap: 44px;
  grid-row-gap: 44px;
  width: 100%;
}
.compare-wrapper-mobile {
  
}
.div1 { grid-area: 1 / 2 / 2 / 3; }
.div2 { grid-area: 1 / 3 / 2 / 4; }
.div3 { grid-area: 1 / 4 / 2 / 5; }
.div4 { grid-area: 2 / 1 / 3 / 2; }
.div5 { grid-area: 2 / 2 / 3 / 3; }
.div6 { grid-area: 2 / 3 / 3 / 4; }
.div7 { grid-area: 2 / 4 / 3 / 5; }

.close {
  cursor: pointer;
}
.element-mobile {
}

.loader {
  margin: 0 auto;
}
.slide-active {
}

.element,
.element-mobile {
  max-width: 180px;
    .close-button {
        cursor: pointer;
    }
    .photo-wrapper {
        position: relative;
        .photo {
          width: 100%;
          height: 140px;  
        }
        .icon {
            display: block;
            width: 24px !important;
            height: 24px !important;
            position: absolute;
            top: 7px;
            right: 11px;
            cursor: pointer;
        }
    }
    .id-wrapper {
        .donor-name {
          color: var(--bank-primary, #00205B);
          @include nsb-gentium-bold;
          font-size: 23px;
          font-style: normal;
          font-weight: 700;
          line-height: 145%; /* 33.35px */
        }
        .id {
          display: inline-block;
          @include nsb-inter-regular;
          font-size: 14px;
          line-height: 17px;
          border-radius: 12px;
          background: $ds-primary-9;
          width: auto;
        }
    }
    .product-name {
        @include nsb-font-container;
        font-size: 14px;
        line-height: 21px;
    }
    .price {
        
      @include nsb-inter-regular;
          font-size: 14px;
          line-height: 17px;
    }
    .close {
        
    }
    .legend-header {
      @include nsb-font-container-medium;
      font-size: 14px;
      line-height: 21px;
      min-height: 42px;
    }
    .data-wrapper {

      .data {
        @include nsb-font-container;
        font-size: 14px;
        line-height: 21px;
        .sub-entry {
            font-size: 10px;
            line-height: 15px;
            color: $nsb-disabled-text;
        }
      }
    }
}

.legend-header {
    @include nsb-font-container-medium;
    font-size: 14px;
    line-height: 21px;
    min-height: 42px;
}
.legend-data-wrapper {
  .data {
    @include nsb-font-container;
    font-size: 14px;
    line-height: 21px;
    color: $ds-primary-3 !important;
  }
}

.info-wrapper {
  .info{
    height: 350px;
    .header {
      @include nsb-font-header-bold;
      font-size: 20px !important;
      line-height: 30px !important;
    }
    .subheader {
      @include nsb-font-header;
      font-size: 14px !important;
      line-height: 22px !important;
    }
    &.mobile {
      .header {
        font-size: 14px !important;
        line-height: 22px !important;
      }
      .subheader {
        font-size: 12px !important;
        line-height: 18px !important;
      }
    }
  }
}
        
</style>